import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, catchError, forkJoin, of, takeUntil } from 'rxjs';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { OrigemLancamentoEnum } from '@shared/enums/compras/origem-lancamento.enum';
import { TipoIntegracaoEnum, TipoIntegracaoIndicador } from '@shared/enums/compras/tipo-integracao.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { OrquestradorCompraService } from '@shared/services/apis/orquestrador-compras/compra.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { CompraIntegracaoExcluirRequest } from '@shared/services/requests/orquestrador-compras/compra-integracao-excluir.request';
import { NFeEntradaEstoqueExcluirRequest } from '@shared/services/requests/orquestrador-compras/nfe-entrada-estoque-excluir.request';
import { NFeEntradaFinanceiroExcluirRequest } from '@shared/services/requests/orquestrador-compras/nfe-entrada-financeiro-excluir.request';
import { CompraEstoqueSituacaoResponse } from '@shared/services/responses/orquestrador-compras/compra-estoque-situacao.response';
import { CompraFinanceiroSituacaoResponse } from '@shared/services/responses/orquestrador-compras/compra-financeiro-situacao.response';
import { NFeEntradaEstoqueSituacaoResponse } from '@shared/services/responses/orquestrador-compras/nfe-entrada-estoque-situacao.response';
import { NFeEntradaFinanceiroSituacaoResponse } from '@shared/services/responses/orquestrador-compras/nfe-entrada-financeiro-situacao.response';
import type { KanbanCard, Person } from '../../../processo-compras.models';
import { FinalizacaoCompraFluxoService } from '../../../services/finalizacao-compra-fluxo.service';
import {
  MovimentacoesEstoqueDialogComponent,
  MovimentacoesEstoqueDialogData,
} from '../../movimentacoes-estoque-dialog/movimentacoes-estoque-dialog.component';
import { CardDetalhe, formatCodigoCompra } from '../card-edit-dialog.types';

/** Estado de um efeito no painel: lançado ou não (a flag do destino), ou sem estado confirmado (consulta falhou). */
enum EstadoEfeitoEnum {
  Lancado = 1,
  NaoLancado = 2,
  Indisponivel = 3,
}

interface EfeitoLancamentoView {
  tipo: TipoIntegracaoEnum;
  /**
   * Bloco do efeito: a origem congelada na entrada em Finalizado ou, sem automação, a que o lançamento manual define
   * (NF quando a compra tem NF ativa, senão o pedido).
   */
  origem: OrigemLancamentoEnum;
  /** Origem nula na compra (automação desativada ao finalizar): não lançado não conta como pendência no resumo. */
  isSemAutomacao?: boolean;
  estado: EstadoEfeitoEnum;
  /** Estoque: quantidade de movimentações; financeiro: número do documento. */
  referencia: string;
  /** Estoque lançado: movimentações do lançamento (abre o detalhe pelo chip da referência). */
  idsEstoqueMovimentacao?: number[];
  /** Motivo de "Excluir" bloqueado (tooltip); vazio = liberado. */
  motivoBloqueioExcluir: string;
  /** Estado não confirmado: motivo exibido na linha e no "Lançar". */
  motivoIndisponivel: string;
}

/** NF do pedido (rótulo resolvido pelo shell, mutado no lugar quando o número chega). */
export interface NotaFinalizado {
  idNFeEntrada: number;
  rotulo: string;
}

/**
 * Etapa Finalizado: estoque e financeiro pela origem congelada na entrada em Finalizado
 * (`compra.idOrigemLancamentoEstoque`/`...Financeiro`). Origem NF: um bloco por NF do pedido, com a integração da
 * NF-e; origem Pedido: um bloco do pedido, com a integração da compra; sem origem (automação desativada): lançamento
 * manual no bloco de cada NF ou, sem NF ativa, no do pedido. Estado real consultado no orquestrador, "Lançar" e
 * "Excluir" por efeito (excluir com justificativa), data e autor da finalização (somente leitura) e observação de
 * encerramento, a única edição em Finalizado.
 *
 * Permissões: ação sobre a nota exige a permissão da NF-e de entrada (Inserir para lançar; Excluir estoque/Excluir
 * financeiro para excluir); ação da própria compra (origem Pedido) exige a do Processo de Compras.
 *
 * Contrato com o shell:
 * - `observacao` é espelhada via `(observacaoChange)`; o shell marca o card como alterado e salva.
 * - `alterado` avisa que um efeito foi lançado ou excluído (o shell recarrega o histórico e o quadro).
 */
@Component({
  selector: 'app-ced-stage-finalizado',
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './ced-stage-finalizado.component.html',
  styleUrl: './ced-stage-finalizado.component.css',
})
export class CedStageFinalizadoComponent implements OnInit, OnDestroy {
  @Input({ required: true }) card!: KanbanCard;
  @Input() detalhe?: CardDetalhe;
  @Input() usersById?: Map<number, Person>;
  /** NFs ativas do pedido (`notasPedido` do shell). */
  @Input() notas: NotaFinalizado[] = [];
  @Input() observacao = '';

  @Output() observacaoChange = new EventEmitter<string>();
  @Output() alterado = new EventEmitter<void>();
  /** Estado dos lançamentos em carga: o shell cobre o corpo do card com o loader de página. */
  @Output() carregandoChange = new EventEmitter<boolean>();

  readonly tipoIntegracao = TipoIntegracaoEnum;
  readonly estadoEfeito = EstadoEfeitoEnum;

  /** Efeitos sem origem: automação desativada na entrada em Finalizado; lançamento manual define a origem. */
  tiposSemOrigem: TipoIntegracaoEnum[] = [];
  /** Efeitos exibidos no bloco de cada NF (origem NF e, com NF ativa, os sem origem). */
  tiposNota: TipoIntegracaoEnum[] = [];
  /** Efeitos exibidos no bloco do pedido (origem Pedido e, sem NF ativa, os sem origem). */
  tiposPedido: TipoIntegracaoEnum[] = [];

  /** Efeitos do bloco de cada NF; vazio enquanto o estado carrega. */
  efeitosPorNota = new Map<number, EfeitoLancamentoView[]>();
  /** Efeitos do bloco do pedido; vazio enquanto o estado carrega. */
  efeitosPedido: EfeitoLancamentoView[] = [];
  carregando = true;
  /** Efeito com "Lançar" em andamento; `idNFeEntrada` nulo = efeito do pedido. */
  lancando: { idNFeEntrada: number | null; tipo: TipoIntegracaoEnum } | null = null;
  /** Efeito com "Excluir" em andamento; `idNFeEntrada` nulo = efeito do pedido. */
  excluindo: { idNFeEntrada: number | null; tipo: TipoIntegracaoEnum } | null = null;

  dataFinalizacao = '—';
  finalizadoPor = '—';
  isSemNotaFiscal = false;
  motivoSemNotaFiscal = '';
  /** "Pedido #24": cabeçalho do bloco do pedido e texto da exclusão. */
  rotuloPedido = '';

  private listaPermissoes: number[] = [];
  /** Origem de cada efeito na compra; nula = automação desativada (o lançamento manual define a origem). */
  private readonly origemPorTipo = new Map<TipoIntegracaoEnum, OrigemLancamentoEnum | null>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private notasService: NotaFiscalEntradaService,
    private orquestradorCompraService: OrquestradorCompraService,
    private finalizacaoFluxo: FinalizacaoCompraFluxoService,
    private authService: AuthService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.listaPermissoes = this.authService.getUserInfos()?.listaPermissoes ?? [];

    const compra = this.detalhe?.compra;
    this.origemPorTipo.set(TipoIntegracaoEnum.Estoque, compra?.idOrigemLancamentoEstoque ?? null);
    this.origemPorTipo.set(TipoIntegracaoEnum.Financeiro, compra?.idOrigemLancamentoFinanceiro ?? null);
    this.aplicarOrigens();
    this.isSemNotaFiscal = !!compra?.isSemNotaFiscal;
    this.motivoSemNotaFiscal = compra?.motivoSemNotaFiscal || '';
    this.rotuloPedido = `Pedido ${formatCodigoCompra(compra?.codigo) || this.card.id}`;

    this.montarFinalizacao();
    this.carregarEstado();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Linhas renderizadas em "Automações executadas": os efeitos do bloco de cada NF e os do bloco do pedido. */
  get quantidadeAutomacoes(): number {
    return this.notas.length * this.tiposNota.length + this.tiposPedido.length;
  }

  /**
   * Mensagem do topo: sem lançamento, tudo lançado ou algo por lançar. `null` enquanto carrega. Efeito sem automação
   * não lançado não conta; lançado conta como os demais.
   */
  get resumo(): { isPendente: boolean; titulo: string; texto: string } | null {
    if (this.carregando) return null;
    const efeitos = [
      ...this.notas.flatMap(n => this.efeitosPorNota.get(n.idNFeEntrada) ?? []),
      ...this.efeitosPedido,
    ];
    if (efeitos.some(e => !e.isSemAutomacao && e.estado !== EstadoEfeitoEnum.Lancado)) {
      return {
        isPendente: true,
        titulo: 'Lançamento a concluir',
        texto: 'Há estoque ou financeiro não lançado ou sem estado confirmado. Confira as automações abaixo.',
      };
    }
    const lancados = efeitos.filter(e => e.estado === EstadoEfeitoEnum.Lancado);
    if (!lancados.length) {
      let texto = 'Nenhum lançamento automático configurado.';
      if (this.tiposSemOrigem.length) texto = 'Lançamentos automáticos desativados; lance manualmente se precisar.';
      else if (this.isSemNotaFiscal) texto = 'Pedido sem nota fiscal: não há lançamento de estoque nem de financeiro.';
      return { isPendente: false, titulo: 'Processo finalizado', texto };
    }
    const tiposLancados = new Set(lancados.map(e => e.tipo));
    let texto = tiposLancados.size > 1
      ? 'Estoque e financeiro lançados.'
      : tiposLancados.has(TipoIntegracaoEnum.Estoque) ? 'Estoque lançado.' : 'Financeiro lançado.';
    // Sem automação e já lançado: o Kanban não lançou (o lançamento manual por ele grava a origem na compra).
    if (lancados.some(e => e.isSemAutomacao)) {
      texto += lancados.some(e => e.isSemAutomacao && e.origem === OrigemLancamentoEnum.NotaFiscal)
        ? ' Lançado fora do Kanban (Central de NFs).'
        : ' Lançado fora do Kanban.';
    }
    return { isPendente: false, titulo: 'Processo finalizado', texto };
  }

  /** Motivo de "Lançar" do efeito bloqueado (tooltip); vazio = liberado. */
  motivoBloqueioLancar(efeito: EfeitoLancamentoView): string {
    if (!this.podeLancar(efeito.origem)) {
      return efeito.origem === OrigemLancamentoEnum.NotaFiscal
        ? 'Seu perfil não tem a permissão Inserir das notas fiscais de entrada.'
        : 'Seu perfil não tem a permissão Inserir do Processo de Compras.';
    }
    if (efeito.estado === EstadoEfeitoEnum.Indisponivel) return efeito.motivoIndisponivel;
    if (this.isProcessando) return 'Aguarde o fim da ação em andamento.';
    return '';
  }

  get isProcessando(): boolean {
    return this.lancando !== null || this.excluindo !== null;
  }

  isLancando(idNFeEntrada: number | null, tipo: TipoIntegracaoEnum): boolean {
    return !!this.lancando && this.lancando.idNFeEntrada === idNFeEntrada && this.lancando.tipo === tipo;
  }

  isExcluindo(idNFeEntrada: number | null, tipo: TipoIntegracaoEnum): boolean {
    return !!this.excluindo && this.excluindo.idNFeEntrada === idNFeEntrada && this.excluindo.tipo === tipo;
  }

  nomeEfeito(tipo: TipoIntegracaoEnum): string {
    return TipoIntegracaoIndicador[tipo].nome;
  }

  rotuloEstado(estado: EstadoEfeitoEnum): string {
    switch (estado) {
      case EstadoEfeitoEnum.Lancado: return 'Lançado';
      case EstadoEfeitoEnum.NaoLancado: return 'Não lançado';
      default: return 'Sem confirmação';
    }
  }

  classeEstado(estado: EstadoEfeitoEnum): string {
    switch (estado) {
      case EstadoEfeitoEnum.Lancado: return 'badge-verde';
      case EstadoEfeitoEnum.NaoLancado: return 'badge-amarelo';
      default: return 'badge-cinza';
    }
  }

  /**
   * Motivo da última tentativa falha do efeito nesta sessão (finalização pelo quadro ou "Lançar"), guardado pelo
   * fluxo; só para efeito não lançado. `nota` nula = efeito do pedido.
   */
  erroEfeito(efeito: EfeitoLancamentoView, nota: NotaFinalizado | null): string | null {
    if (efeito.estado !== EstadoEfeitoEnum.NaoLancado) return null;
    const id = nota ? nota.idNFeEntrada : this.card.idCompra;
    if (!id) return null;
    const erros = this.finalizacaoFluxo.ultimoErro(efeito.origem, id);
    return (efeito.tipo === TipoIntegracaoEnum.Estoque ? erros?.estoque : erros?.financeiro) ?? null;
  }

  descricaoEfeito(efeito: EfeitoLancamentoView): string {
    const isEstoque = efeito.tipo === TipoIntegracaoEnum.Estoque;
    const isPedido = efeito.origem === OrigemLancamentoEnum.Pedido;
    if (efeito.isSemAutomacao && efeito.estado === EstadoEfeitoEnum.NaoLancado) return 'Automação desativada: lance manualmente.';
    if (efeito.isSemAutomacao && efeito.estado === EstadoEfeitoEnum.Lancado) {
      return isPedido ? 'Lançado fora do Kanban.' : 'Lançado fora do Kanban (Central de NFs).';
    }
    switch (efeito.estado) {
      case EstadoEfeitoEnum.Lancado:
        if (isPedido) {
          return isEstoque
            ? 'Entrada no estoque com as quantidades do pedido.'
            : 'Contas a pagar com fornecedor, valor e parcelas do pedido.';
        }
        return isEstoque
          ? 'Entrada no estoque com as quantidades conferidas da NF.'
          : 'Contas a pagar com fornecedor, valor e vencimentos da NF.';
      case EstadoEfeitoEnum.NaoLancado:
        return isEstoque ? 'Entrada no estoque ainda não lançada.' : 'Financeiro ainda não lançado.';
      default:
        return efeito.motivoIndisponivel;
    }
  }

  /** Chip "N movimentações": lista as movimentações do efeito de estoque. `nota` nula = efeito do pedido. */
  verMovimentacoes(efeito: EfeitoLancamentoView, nota: NotaFinalizado | null): void {
    const idsEstoqueMovimentacao = efeito.idsEstoqueMovimentacao ?? [];
    if (!idsEstoqueMovimentacao.length) return;
    this.dialog.open<MovimentacoesEstoqueDialogComponent, MovimentacoesEstoqueDialogData>(MovimentacoesEstoqueDialogComponent, {
      width: '900px',
      maxWidth: '96vw',
      autoFocus: false,
      data: {
        titulo: `Movimentações de estoque · ${nota ? nota.rotulo : this.rotuloPedido}`,
        idsEstoqueMovimentacao,
      },
    });
  }

  /** Lança só este efeito, pela NF ou, com `nota` nula, pelo pedido, e relê o estado. O fluxo guarda ou limpa o erro. */
  async lancarEfeito(efeito: EfeitoLancamentoView, nota: NotaFinalizado | null): Promise<void> {
    const idCompra = this.card.idCompra;
    if (efeito.estado === EstadoEfeitoEnum.Lancado || this.motivoBloqueioLancar(efeito) || (!nota && !idCompra)) return;
    const isEstoque = efeito.tipo === TipoIntegracaoEnum.Estoque;
    const efeitos = { estoque: isEstoque, financeiro: efeito.tipo === TipoIntegracaoEnum.Financeiro };
    this.lancando = { idNFeEntrada: nota?.idNFeEntrada ?? null, tipo: efeito.tipo };
    const resultado = nota
      ? (await this.finalizacaoFluxo.lancarNotas([nota.idNFeEntrada], efeitos))[0]
      : await this.finalizacaoFluxo.lancarPedido(idCompra!, efeitos);
    this.lancando = null;

    const lancamento = isEstoque ? resultado?.estoque : resultado?.financeiro;
    if (lancamento?.isLancado) {
      // Sem automação: o backend gravou na compra a origem do lançamento (NF ou pedido); o detalhe do shell não é
      // relido aqui, então o painel passa a tratar o efeito pela origem nova.
      if (efeito.isSemAutomacao) {
        this.origemPorTipo.set(efeito.tipo, efeito.origem);
        this.aplicarOrigens();
      }
      this.alertService.show('Sucesso', isEstoque ? 'Estoque lançado.' : 'Financeiro lançado.', AlertType.success);
      if (lancamento.aviso) this.alertService.show('Atenção', lancamento.aviso, AlertType.warning);
      this.alterado.emit();
    } else {
      this.alertService.show('Atenção',
        `${this.nomeEfeito(efeito.tipo)}: ${lancamento?.erro || 'não foi possível lançar.'}`, AlertType.warning);
    }
    this.carregarEstado();
  }

  /**
   * Exclui um efeito com justificativa obrigatória. `nota` nula = efeito de origem Pedido (exclusão pela compra,
   * registrada no histórico dela); senão, pela NF (registrada no histórico da nota pelo orquestrador).
   */
  excluir(efeito: EfeitoLancamentoView, nota: NotaFinalizado | null): void {
    const idCompra = this.card.idCompra;
    if (this.isProcessando || efeito.motivoBloqueioExcluir || (!nota && !idCompra)) return;
    const isEstoque = efeito.tipo === TipoIntegracaoEnum.Estoque;
    const efeitoTexto = isEstoque ? 'as <b>movimentações de estoque</b>' : 'as <b>contas a pagar</b>';
    const innerHtml = nota
      ? `Ao excluir pela ${nota.rotulo}, ${efeitoTexto} lançadas por ela são excluídas. A justificativa é registrada no `
        + 'histórico da nota. O vínculo com a NF-e só é liberado depois de excluir o estoque e o financeiro dela.'
      : `Ao excluir pelo ${this.rotuloPedido}, ${efeitoTexto} lançadas por ele são excluídas. A justificativa é registrada `
        + 'no histórico do pedido.';
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: isEstoque ? 'Excluir estoque' : 'Excluir financeiro',
        innerHtml,
        buttonConfirm: 'Excluir',
        buttonClose: 'Voltar',
        showTextarea: true,
        textareaLabel: 'Justificativa',
        textareaRequired: true,
        textareaMinLength: 10,
      },
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(resultado => {
      if (!resultado?.confirm) return;
      const justificativa = (resultado.textareaValue as string).trim();
      let exclusao$: Observable<{ isError: boolean; errorDescription?: string | null; avisoDescricao?: string | null }>;
      if (nota) {
        if (isEstoque) {
          const request = new NFeEntradaEstoqueExcluirRequest();
          request.justificativa = justificativa;
          exclusao$ = this.notasService.excluirEstoque(nota.idNFeEntrada, request);
        } else {
          const request = new NFeEntradaFinanceiroExcluirRequest();
          request.justificativa = justificativa;
          exclusao$ = this.notasService.excluirFinanceiro(nota.idNFeEntrada, request);
        }
      } else {
        const request = new CompraIntegracaoExcluirRequest();
        request.justificativa = justificativa;
        exclusao$ = isEstoque
          ? this.orquestradorCompraService.excluirEstoque(idCompra!, request)
          : this.orquestradorCompraService.excluirFinanceiro(idCompra!, request);
      }
      const erroPadrao = isEstoque ? 'Não foi possível excluir o estoque.' : 'Não foi possível excluir o financeiro.';
      this.excluindo = { idNFeEntrada: nota?.idNFeEntrada ?? null, tipo: efeito.tipo };
      exclusao$.pipe(takeUntil(this.destroy$)).subscribe({
        next: resposta => {
          this.excluindo = null;
          if (resposta.isError) {
            this.alertService.show('Atenção', resposta.errorDescription || erroPadrao, AlertType.warning);
            this.carregarEstado();
            return;
          }
          this.alertService.show('Sucesso', isEstoque ? 'Estoque excluído.' : 'Financeiro excluído.', AlertType.success);
          if (resposta.avisoDescricao) this.alertService.show('Atenção', resposta.avisoDescricao, AlertType.warning);
          this.alterado.emit();
          this.carregarEstado();
        },
        error: () => {
          this.excluindo = null;
          this.alertService.show('Erro', erroPadrao, AlertType.error);
          this.carregarEstado();
        },
      });
    });
  }

  /** Lançar: Inserir da NF-e de entrada pela nota; Inserir do Processo de Compras pelo pedido. */
  private podeLancar(origem: OrigemLancamentoEnum): boolean {
    const permissao = origem === OrigemLancamentoEnum.NotaFiscal
      ? PermissaoEnum.Suprimentos_NotasFiscaisEntrada_Inserir
      : PermissaoEnum.Suprimentos_ProcessoCompras_Inserir;
    return this.listaPermissoes.includes(permissao);
  }

  /** Excluir: Excluir estoque/financeiro da NF-e de entrada pela nota; do Processo de Compras pelo pedido. */
  private motivoSemPermissaoExcluir(tipo: TipoIntegracaoEnum, origem: OrigemLancamentoEnum): string {
    const isEstoque = tipo === TipoIntegracaoEnum.Estoque;
    const daCompra = isEstoque
      ? PermissaoEnum.Suprimentos_ProcessoCompras_ExcluirEstoque
      : PermissaoEnum.Suprimentos_ProcessoCompras_ExcluirFinanceiro;
    const daNota = isEstoque
      ? PermissaoEnum.Suprimentos_NotasFiscaisEntrada_ExcluirEstoque
      : PermissaoEnum.Suprimentos_NotasFiscaisEntrada_ExcluirFinanceiro;
    const isNota = origem === OrigemLancamentoEnum.NotaFiscal;
    if (this.listaPermissoes.includes(isNota ? daNota : daCompra)) return '';
    const nomePermissao = isEstoque ? 'Excluir estoque' : 'Excluir financeiro';
    return isNota
      ? `Seu perfil não tem a permissão ${nomePermissao} das notas fiscais de entrada.`
      : `Seu perfil não tem a permissão ${nomePermissao} do Processo de Compras.`;
  }

  /**
   * Distribui os efeitos pela origem: NF no bloco de cada NF, Pedido no bloco do pedido e, sem origem, no bloco de
   * cada NF quando a compra tem NF ativa, senão no do pedido (onde o lançamento manual define a origem).
   */
  private aplicarOrigens(): void {
    const origens = [...this.origemPorTipo];
    const isSemOrigemPelaNota = this.notas.length > 0;
    this.tiposSemOrigem = origens.filter(([, origem]) => origem == null).map(([tipo]) => tipo);
    this.tiposNota = origens
      .filter(([, origem]) => origem === OrigemLancamentoEnum.NotaFiscal || (origem == null && isSemOrigemPelaNota))
      .map(([tipo]) => tipo);
    this.tiposPedido = origens
      .filter(([, origem]) => origem === OrigemLancamentoEnum.Pedido || (origem == null && !isSemOrigemPelaNota))
      .map(([tipo]) => tipo);
  }

  /** Data e autor gravados na entrada em Finalizado; compras finalizadas antes da coluna ficam com "—". */
  private montarFinalizacao(): void {
    const datetimeFinalizacao = this.detalhe?.datetimeFinalizacao;
    this.dataFinalizacao = datetimeFinalizacao
      ? new Date(datetimeFinalizacao).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
      : '—';
    const idUsuario = this.detalhe?.idUsuarioFinalizacao;
    this.finalizadoPor = idUsuario ? (this.usersById?.get(idUsuario)?.name || `Usuário ${idUsuario}`) : '—';
  }

  private carregarEstado(): void {
    const idsNFeEntrada = this.tiposNota.length ? this.notas.map(n => n.idNFeEntrada) : [];
    const idCompra = this.tiposPedido.length ? this.card.idCompra : undefined;
    if (!idsNFeEntrada.length && !idCompra) {
      this.efeitosPorNota = new Map();
      this.efeitosPedido = [];
      this.carregando = false;
      this.carregandoChange.emit(false);
      return;
    }
    // Só consulta o efeito exibido no bloco: o outro não aparece nele.
    const isEstoqueNota = idsNFeEntrada.length > 0 && this.tiposNota.includes(TipoIntegracaoEnum.Estoque);
    const isFinanceiroNota = idsNFeEntrada.length > 0 && this.tiposNota.includes(TipoIntegracaoEnum.Financeiro);
    const isEstoquePedido = !!idCompra && this.tiposPedido.includes(TipoIntegracaoEnum.Estoque);
    const isFinanceiroPedido = !!idCompra && this.tiposPedido.includes(TipoIntegracaoEnum.Financeiro);
    const tiposSemOrigem = this.tiposSemOrigem;
    this.carregando = true;
    this.carregandoChange.emit(true);
    forkJoin({
      estoqueNota: isEstoqueNota
        ? this.notasService.consultarSituacaoEstoque({ idsNFeEntrada }).pipe(catchError(() => of(null)))
        : of(null),
      financeiroNota: isFinanceiroNota
        ? this.notasService.consultarSituacaoFinanceira({ idsNFeEntrada }).pipe(catchError(() => of(null)))
        : of(null),
      estoquePedido: isEstoquePedido
        ? this.orquestradorCompraService.consultarSituacaoEstoque([idCompra!]).pipe(catchError(() => of(null)))
        : of(null),
      financeiroPedido: isFinanceiroPedido
        ? this.orquestradorCompraService.consultarSituacaoFinanceira([idCompra!]).pipe(catchError(() => of(null)))
        : of(null),
    }).pipe(takeUntil(this.destroy$)).subscribe(({ estoqueNota, financeiroNota, estoquePedido, financeiroPedido }) => {
      this.carregando = false;
      this.carregandoChange.emit(false);
      const consultas = [
        { isConsultada: isEstoqueNota, resposta: estoqueNota },
        { isConsultada: isFinanceiroNota, resposta: financeiroNota },
        { isConsultada: isEstoquePedido, resposta: estoquePedido },
        { isConsultada: isFinanceiroPedido, resposta: financeiroPedido },
      ];
      if (consultas.some(c => c.isConsultada && (!c.resposta || c.resposta.isError))) {
        this.alertService.show('Erro', 'Não foi possível consultar o estoque e o financeiro lançados.', AlertType.error);
      }
      // Sem origem e já lançado: lançado fora do Kanban (o lançamento manual por ele grava a origem na compra).
      // A exclusão fica com a rotina que lançou; "Excluir" bloqueia mesmo com as permissões do Kanban.
      const comAutomacao = (efeito: EfeitoLancamentoView): EfeitoLancamentoView => {
        const isSemAutomacao = tiposSemOrigem.includes(efeito.tipo);
        if (!isSemAutomacao || efeito.estado !== EstadoEfeitoEnum.Lancado) return { ...efeito, isSemAutomacao };
        const motivoBloqueioExcluir = efeito.origem === OrigemLancamentoEnum.NotaFiscal
          ? 'Lançado fora do Kanban (Central de NFs). Exclua pela Central de NFs.'
          : 'Lançado fora do Kanban. Exclua pela rotina em que foi lançado.';
        return { ...efeito, isSemAutomacao, motivoBloqueioExcluir };
      };
      this.efeitosPorNota = new Map(idsNFeEntrada.map(id => [id, this.tiposNota.map(tipo => comAutomacao(
        tipo === TipoIntegracaoEnum.Estoque ? this.efeitoEstoqueNota(id, estoqueNota) : this.efeitoFinanceiroNota(id, financeiroNota)))]));
      this.efeitosPedido = idCompra
        ? this.tiposPedido.map(tipo => comAutomacao(tipo === TipoIntegracaoEnum.Estoque
          ? this.efeitoEstoquePedido(idCompra, estoquePedido)
          : this.efeitoFinanceiroPedido(idCompra, financeiroPedido)))
        : [];
    });
  }

  private efeitoEstoqueNota(idNFeEntrada: number, resposta: NFeEntradaEstoqueSituacaoResponse | null): EfeitoLancamentoView {
    const origem = OrigemLancamentoEnum.NotaFiscal;
    if (!resposta || resposta.isError) {
      return this.efeitoIndisponivel(TipoIntegracaoEnum.Estoque, origem, 'Não foi possível confirmar o estoque lançado pela nota.');
    }
    const resumo = resposta.lista?.find(s => s.idNFeEntrada === idNFeEntrada);
    if (!resumo?.temMovimentacaoEstoque) return this.efeitoNaoLancado(TipoIntegracaoEnum.Estoque, origem);
    return this.efeitoEstoqueLancado(origem, resumo.idsEstoqueMovimentacao ?? []);
  }

  private efeitoFinanceiroNota(idNFeEntrada: number, resposta: NFeEntradaFinanceiroSituacaoResponse | null): EfeitoLancamentoView {
    const origem = OrigemLancamentoEnum.NotaFiscal;
    if (!resposta || resposta.isError) {
      return this.efeitoIndisponivel(TipoIntegracaoEnum.Financeiro, origem, 'Não foi possível confirmar o financeiro lançado pela nota.');
    }
    if (!(resposta.idsNFeEntradaComConta ?? []).includes(idNFeEntrada)) {
      return this.efeitoNaoLancado(TipoIntegracaoEnum.Financeiro, origem);
    }
    const documentos = (resposta.contas ?? []).filter(c => c.idNFeEntrada === idNFeEntrada).map(c => c.numeroDocumento);
    return this.efeitoFinanceiroLancado(origem, documentos, (resposta.idsNFeEntradaComLiquidacao ?? []).includes(idNFeEntrada));
  }

  private efeitoEstoquePedido(idCompra: number, resposta: CompraEstoqueSituacaoResponse | null): EfeitoLancamentoView {
    const origem = OrigemLancamentoEnum.Pedido;
    if (!resposta || resposta.isError) {
      return this.efeitoIndisponivel(TipoIntegracaoEnum.Estoque, origem, 'Não foi possível confirmar o estoque lançado pelo pedido.');
    }
    const resumo = resposta.lista?.find(s => s.idCompra === idCompra);
    if (!resumo?.temMovimentacaoEstoque) return this.efeitoNaoLancado(TipoIntegracaoEnum.Estoque, origem);
    return this.efeitoEstoqueLancado(origem, resumo.idsEstoqueMovimentacao ?? []);
  }

  private efeitoFinanceiroPedido(idCompra: number, resposta: CompraFinanceiroSituacaoResponse | null): EfeitoLancamentoView {
    const origem = OrigemLancamentoEnum.Pedido;
    if (!resposta || resposta.isError) {
      return this.efeitoIndisponivel(TipoIntegracaoEnum.Financeiro, origem, 'Não foi possível confirmar o financeiro lançado pelo pedido.');
    }
    if (!(resposta.idsCompraComConta ?? []).includes(idCompra)) {
      return this.efeitoNaoLancado(TipoIntegracaoEnum.Financeiro, origem);
    }
    const documentos = (resposta.contas ?? []).filter(c => c.idCompra === idCompra).map(c => c.numeroDocumento);
    return this.efeitoFinanceiroLancado(origem, documentos, (resposta.idsCompraComLiquidacao ?? []).includes(idCompra));
  }

  /** Movimentação de estoque não tem código de documento: a referência é a quantidade de movimentações. */
  private efeitoEstoqueLancado(origem: OrigemLancamentoEnum, idsEstoqueMovimentacao: number[]): EfeitoLancamentoView {
    const quantidade = idsEstoqueMovimentacao.length;
    return {
      tipo: TipoIntegracaoEnum.Estoque,
      origem,
      estado: EstadoEfeitoEnum.Lancado,
      referencia: quantidade ? `${quantidade} ${quantidade === 1 ? 'movimentação' : 'movimentações'}` : '',
      idsEstoqueMovimentacao,
      motivoBloqueioExcluir: this.motivoSemPermissaoExcluir(TipoIntegracaoEnum.Estoque, origem),
      motivoIndisponivel: '',
    };
  }

  private efeitoFinanceiroLancado(
    origem: OrigemLancamentoEnum,
    numerosDocumento: (string | null | undefined)[],
    isLiquidada: boolean,
  ): EfeitoLancamentoView {
    const documentos = [...new Set(numerosDocumento.filter(Boolean))];
    return {
      tipo: TipoIntegracaoEnum.Financeiro,
      origem,
      estado: EstadoEfeitoEnum.Lancado,
      referencia: documentos.length ? `Doc. ${documentos.join(', ')}` : '',
      motivoBloqueioExcluir: this.motivoSemPermissaoExcluir(TipoIntegracaoEnum.Financeiro, origem)
        || (isLiquidada ? 'A conta tem pagamento ou conciliação: estorne o pagamento no Finanças antes de excluir.' : ''),
      motivoIndisponivel: '',
    };
  }

  private efeitoNaoLancado(tipo: TipoIntegracaoEnum, origem: OrigemLancamentoEnum): EfeitoLancamentoView {
    return { tipo, origem, estado: EstadoEfeitoEnum.NaoLancado, referencia: '', motivoBloqueioExcluir: '', motivoIndisponivel: '' };
  }

  private efeitoIndisponivel(tipo: TipoIntegracaoEnum, origem: OrigemLancamentoEnum, motivo: string): EfeitoLancamentoView {
    return { tipo, origem, estado: EstadoEfeitoEnum.Indisponivel, referencia: '', motivoBloqueioExcluir: motivo, motivoIndisponivel: motivo };
  }
}
